/**
 * Adapter `OperatorSupportApi` per l'area rappresentante.
 * Wrappa le funzioni di `representativeSupportService.ts` (token rappresentante
 * iniettato da `repAxios`): nessuna logica nuova, solo l'adesione all'interfaccia.
 */

import * as rep from '../representativeSupportService';
import { getRepAssociates } from '../representativeService';
import type {
  ActorType,
  Assignee,
  AssociateSearchResult,
  CampiExtra,
  Candidate,
  ChannelSettings,
  GroupLite,
  InvitableResult,
  ListParams,
  ListResult,
  Macro,
  Mentionable,
  MentionRef,
  MergeCandidate,
  OperatorSupportApi,
  RelatedTicket,
  SavedView,
  SavedViewFilters,
  ShareLinksPayload,
  ShareLinksResult,
  TicketPrintFile,
  TicketPrintOptions,
  StatsData,
  SupportEmailPrefs,
  SupportEmailPrefsInput,
  TicketDetail,
  TicketListItem,
  TodoData,
} from '../supportApi';

export const representativeSupportApi: OperatorSupportApi = {
  kind: 'representative',

  listTickets: (params: ListParams): Promise<ListResult<TicketListItem>> => rep.listTickets(params),
  getTicket: (id: string): Promise<TicketDetail> => rep.getTicket(id),
  getCandidates: (id: string): Promise<Candidate[]> => rep.getCandidates(id),
  take: (id: string) => rep.take(id),
  setAssignees: (id: string, payload: { add?: Assignee[]; remove?: Assignee[] }) =>
    rep.setAssignees(id, payload),
  reply: (id: string, body: string, files?: File[], thenStatus?: string) => rep.reply(id, body, files, thenStatus),
  // Menzioni (Task 48) solo se presenti: senza, la chiamata e quella di prima.
  addNote: (id: string, body: string, files?: File[], mentions?: MentionRef[]) =>
    (mentions && mentions.length ? rep.addNote(id, body, files, mentions) : rep.addNote(id, body, files)),
  getMentionables: (id: string): Promise<Mentionable[]> => rep.getMentionables(id),
  // Partecipanti invitati (parte B): gate associativa.supporto.invite lato rotta.
  searchInvitable: (id: string, q: string, page?: number): Promise<InvitableResult> => rep.searchInvitable(id, q, page),
  addParticipants: (id: string, add: Assignee[]) => rep.addParticipants(id, add),
  removeParticipant: (id: string, actorType: ActorType, actorId: string) => rep.removeParticipant(id, actorType, actorId),
  // Canali in copia (parte A): gate associativa.supporto.transfer lato rotta.
  addCopyGroups: (id: string, groupIds: string[]) => rep.addCopyGroups(id, groupIds),
  removeCopyGroup: (id: string, groupId: string) => rep.removeCopyGroup(id, groupId),
  setResponsible: (id: string, groupId: string, motivo?: string) => rep.setResponsible(id, groupId, motivo),
  editMessage: (id: string, messageId: string, body: string) => rep.editMessage(id, messageId, body),
  removeAttachment: (id: string, messageId: string, index: number) => rep.removeAttachment(id, messageId, index),
  editSubject: (id: string, subject: string) => rep.editSubject(id, subject),
  setCampiExtra: (id: string, campiExtra: CampiExtra) => rep.setCampiExtra(id, campiExtra),
  editNote: (id: string, noteId: string, body: string) => rep.editNote(id, noteId, body),
  deleteNote: (id: string, noteId: string) => rep.deleteNote(id, noteId),
  setStatus: (id: string, status: string) => rep.setStatus(id, status),
  closeTicket: (id: string, motivo?: string) => rep.closeTicket(id, motivo),
  // Cambio del richiedente: la rotta risponde 403 senza il permesso dedicato,
  // e la UI mostra la voce solo a chi ce l'ha.
  setRequester: (id: string, payload: { type: 'guest' | 'associate'; email?: string; nome?: string; associateId?: string }) =>
    rep.setRequester(id, payload),
  // Ricerca nel solo ambito territoriale del rappresentante.
  searchAssociates: async (term: string) => {
    const res = await getRepAssociates({ search: term, limit: '10' });
    return (res?.data || []) as AssociateSearchResult[];
  },
  transfer: (id: string, toGroupId: string, motivo: string, copyGroupIds?: string[]) =>
    (copyGroupIds && copyGroupIds.length ? rep.transfer(id, toGroupId, motivo, copyGroupIds) : rep.transfer(id, toGroupId, motivo)),
  getMergeCandidates: (id: string): Promise<MergeCandidate[]> => rep.getMergeCandidates(id),
  // Niente setTags/setPriority qui: la loro assenza e il gate lato client, e il
  // backend rappresentante non espone quelle rotte.
  getRelatedTickets: (id: string): Promise<RelatedTicket[]> => rep.getRelatedTickets(id),
  mergeTicket: (id: string, targetTicketId: string) => rep.mergeTicket(id, targetTicketId),
  openAttachment: (id: string, storedName: string, displayName?: string) =>
    rep.openAttachment(id, storedName, displayName),
  getGroups: (): Promise<{ mine: GroupLite[]; all: GroupLite[]; assignedCount?: number; invitedCount?: number }> => rep.getGroups(),
  // Risposte in bozza (Task 46): approvazione/rifiuto col permesso manage_channel,
  // correzione e ritiro solo dell'autore; gestione del flag «puo rispondere».
  approveDraft: (id: string, draftId: string, body?: string) => rep.approveDraft(id, draftId, body),
  rejectDraft: (id: string, draftId: string, note?: string) => rep.rejectDraft(id, draftId, note),
  editDraft: (id: string, draftId: string, body: string) => rep.editDraft(id, draftId, body),
  deleteDraft: (id: string, draftId: string) => rep.deleteDraft(id, draftId),
  getManagedChannels: () => rep.getManagedChannels(),
  setMemberCanReply: (groupId: string, actorId: string, canReply: boolean) => rep.setMemberCanReply(groupId, actorId, canReply),
  setChannelSettings: (groupId: string, patch: Partial<ChannelSettings>) => rep.setChannelSettings(groupId, patch),
  getMacros: (groupId?: string): Promise<Macro[]> => rep.getMacros(groupId),
  getViews: (): Promise<SavedView[]> => rep.getViews(),
  createView: (nome: string, filtri: SavedViewFilters): Promise<SavedView> =>
    rep.createView(nome, filtri),
  deleteView: (id: string) => rep.deleteView(id),
  aiRefine: (text: string, action: string, ticketId?: string) => rep.aiRefine(text, action, ticketId),
  suggestReply: (id: string, draft?: string) => rep.suggestReply(id, draft),
  markSimilarUsed: (cardIds: string[]) => rep.markSimilarUsed(cardIds),
  summarizeTicket: (id: string) => rep.summarizeTicket(id),
  createShareLinks: (id: string, payload: ShareLinksPayload): Promise<ShareLinksResult> =>
    rep.createShareLinks(id, payload),
  printTicket: (id: string, options: TicketPrintOptions): Promise<TicketPrintFile> => rep.printTicket(id, options),
  getStats: (): Promise<StatsData> => rep.getStats(),
  getTodo: (): Promise<TodoData> => rep.getSupportTodo(),
  getNotificationPreferences: (): Promise<SupportEmailPrefs> => rep.getNotificationPreferences(),
  saveNotificationPreferences: (prefs: SupportEmailPrefsInput): Promise<SupportEmailPrefs> => rep.saveNotificationPreferences(prefs),
  sendTestDigest: (): Promise<{ to: string }> => rep.sendTestDigest(),
  revokeSupportLinks: (): Promise<{ linkVersion: number }> => rep.revokeSupportLinks(),
  getNovitaVista: (): Promise<{ versioneVista: string | null }> => rep.getNovitaVista(),
  setNovitaVista: (versione: string): Promise<void> => rep.setNovitaVista(versione),

  ticketPath: (id: string) => `/area-associativa/supporto/${id}`,
};

export default representativeSupportApi;
